import React, { useState, useEffect } from "react";
import { Modal, Row, Card, Col, Button } from "react-bootstrap";
import { AutoComplete } from "antd";
import RecipesCard from "./RecipesCard";
import {
  CONSTANTS,
  MEAL_CATEGORIES,
  MEAL_PLAN_TYPES,
} from "../../utils/Constant/Constant";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import ToastNotification from "../../components/Common/ToastNotification";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRecipesBasedMealType,
  fetchRecipesBasedMealTypeReplaceRecipeModalThunk,
  resetModalPagination,
} from "../../redux/Slices/clientMealsTabsSlice";
import Loader from "../../components/Common/Loader";

const chunkArray = (array, size) => {
  return Array.isArray(array)
    ? array.reduce((acc, _, i) => {
        if (i % size === 0) acc.push(array.slice(i, i + size));
        return acc;
      }, [])
    : [];
};

export const mealPlanMap = {
  [MEAL_PLAN_TYPES.TWO_MEALS]: [MEAL_CATEGORIES.LUNCH, MEAL_CATEGORIES.DINNER],
  [MEAL_PLAN_TYPES.THREE_MEALS]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.DINNER,
  ],
  [MEAL_PLAN_TYPES.THREE_MEALS_ONE_SNACK]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.DINNER,
    MEAL_CATEGORIES.SNACK,
  ],
  [MEAL_PLAN_TYPES.THREE_MEALS_TWO_SNACKS]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.DINNER,
    MEAL_CATEGORIES.SNACK,
    MEAL_CATEGORIES.SNACK1,
  ],
  [MEAL_PLAN_TYPES.FOUR_MEALS]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.LUNCH1,
    MEAL_CATEGORIES.DINNER,
  ],
  [MEAL_PLAN_TYPES.FOUR_MEALS_ONE_SNACK]: [
    MEAL_CATEGORIES.BREAKFAST,
    MEAL_CATEGORIES.LUNCH,
    MEAL_CATEGORIES.DINNER,
    MEAL_CATEGORIES.SNACK,
    MEAL_CATEGORIES.LUNCH1,
  ],
};

const ReplaceRecipeModal = ({
  show,
  onHide,
  selectedRecipes = [],
  onSelectedRecipesChange = () => {},
  handleRecipeUpdate,
  mealType,
  selectedMeals,
  formData,
  exclude,
  mealPercentages,
  allergies,
  dietaryPreferences,
  numberOfRecipesPerMeal,
}) => {
  const dispatch = useDispatch();
  const { trans } = useCustomTranslation();

  const {
    filterReplaceModal,
    filterReplaceModalIsLoading,
    filterReplaceModalError,
    hasMore,
    pageNumber,
    pageSize,
  } = useSelector((state) => state.mealTags);

  const [selectedList, setSelectedList] = useState([]);
  const [indexToReplace, setIndexToReplace] = useState(null);
  const [toastConfig, setToastConfig] = useState({});
  const [recipesList, setRecipesList] = useState();
  const [isUpdating, setIsUpdating] = useState(false);

  const [debounceTimeout, setDebounceTimeout] = useState(null);
  // const [originalRecipesList, setOriginalRecipesList] = useState([]);
  const [filteredRecipesList, setFilteredRecipesList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Helper function to extract recipe data
  const extractRecipeData = (item) => {
    // New format: has mealName and recipe nested object
    if (item.mealName && item.recipe) {
      return {
        id: item.recipe.recipeId,
        title: item.recipe.title,
        mainImage: item.recipe.mainImage || item.recipe.image,
        totalTime: item.recipe.prepTime || item.recipe.totalTime,
        nutrientsPerServing: {
          calories: item.recipe.nutrientsPerserving?.calories,
          protein: item.recipe.nutrientsPerserving?.protein_g,
          carbs: item.recipe.nutrientsPerserving?.carbs_g,
          fat: item.recipe.nutrientsPerserving?.fat_g,
        },
        ingredientList: item.recipe.ingredientList || [],
        course: item.recipe.course,
        servings: item.recipe.servings,
        difficulty: item.recipe.difficulty,
        ingredients: item.recipe.ingredients || [],
        instructions: item.recipe.instructions || [],
        mealName: item.mealName,
        originalItem: item, // Keep reference to original structure
      };
    }

    // Old format: direct recipe properties
    return {
      id: item.id || item.recipeId,
      title: item.title,
      mainImage: item.mainImage || item.image,
      totalTime: item.totalTime || item.prepTime,
      nutrientsPerServing: item.nutrientsPerserving || {},
      course: item.course,
      servings: item.servings,
      difficulty: item.difficulty,
      ingredients: item.ingredients || [],
      instructions: item.instructions || [],
      ingredientList: item.ingredientList || [],
      mealName: item.mealName,
      originalItem: item,
    };
  };

  useEffect(() => {
    setRecipesList(filterReplaceModal?.[selectedMeals] || []);
  }, [filterReplaceModal, selectedMeals]);

  const fetchFilteredRecipes = (search = "", page = 1, size = pageSize) => {
    const percentage = mealPercentages[selectedMeals] || 0;
    const kcal = formData?.dailyIntake
      ? ((formData?.dailyIntake * percentage) / 100)?.toFixed(2)
      : 0;

    const requestPayload = {
      carbs: formData.carbsPercentage.toString(),
      fats: formData.fatPercentage.toString(),
      protein: formData.proteinPercentage.toString(),
      receipeCount: size,
      targetCalories: parseInt(kcal),
      preferences: dietaryPreferences?.join(","),
      allergies: allergies?.join(","),
      recipeName: search,
      pagination: { pageNumber: page, pageSize: size },
      isCustomized: false,
    };

    dispatch(
      fetchRecipesBasedMealTypeReplaceRecipeModalThunk({
        requestPayload,
        mealTime: selectedMeals,
      })
    );
  };

  // New function to handle update with selectedId
  const handleUpdateWithSelectedRecipes = async () => {
    setIsUpdating(true);
    try {
      const originalStructuredData = selectedList.map(
        (item) => item.originalItem
      );

      // Extract recipe IDs from the selected recipes
      const selectedIds = originalStructuredData.map(
        (item) => item.recipe.recipeId
      );

      const percentage = mealPercentages[selectedMeals] || 0;
      const kcal = formData?.dailyIntake
        ? ((formData?.dailyIntake * percentage) / 100)?.toFixed(2)
        : 0;
      // call customized recipes
      const requestPayload = {
        carbs: formData.carbsPercentage.toString(),
        fats: formData.fatPercentage.toString(),
        protein: formData.proteinPercentage.toString(),
        receipeCount: selectedIds.length,
        targetCalories: parseInt(kcal),
        preferences: dietaryPreferences?.join(","),
        allergies: allergies?.join(","),
        recipeName: searchQuery,
        pagination: { pageNumber: 1, pageSize: selectedIds.length },
        isCustomized: true,
        // Add the selectedId array
        selectedRecipeId: selectedIds,
      };

      // Call API with the requestPayload
      const apiResponse = await dispatch(
        fetchRecipesBasedMealType({
          requestPayload,
          mealTime: selectedMeals,
        })
      );

      // Check if API call was successful
      if (apiResponse.type.includes("fulfilled")) {
        // API returned successfully, use the response data
        const { payload } = apiResponse;
        const updatedRecipesData = payload[selectedMeals] || []; // Access the specific meal time array

        // Transform the flat recipe data into the structured format
        const structuredRecipesData = updatedRecipesData.map((recipe) => ({
          mealName: mealType, // Use the current meal type
          recipeId: recipe.recipeId,
          recipe: {
            title: recipe.title,
            prepTime: recipe.prepTime || recipe.totalTime,
            servings: recipe.servings,
            ingredientsCount: recipe.ingredientsCount,
            course: recipe.course,
            difficulty: recipe.difficulty,
            ingredients: recipe.ingredients || [],
            instructions: recipe.instructions || [],
            nutrientsPerServing: recipe.nutrientsPerserving || {},
            recipeId: recipe.recipeId,
            mainImage: recipe.mainImage,
            alergies: recipe.alergies || "",
            preferences: recipe.preferences || "",
            ingredientList: recipe.ingredientList || [],
          },
        }));

        // console.log("test_structuredRecipesData", structuredRecipesData);

        // Pass the structured data to handleRecipeUpdate
        handleRecipeUpdate(structuredRecipesData, mealType);
        // Close modal after successful update
        onHide();
      } else {
        // API call failed
        throw new Error(apiResponse.error?.message || "API call failed");
      }
    } catch (error) {
      console.error("Error updating recipes:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (hasMore && !filterReplaceModalIsLoading) {
      fetchFilteredRecipes(searchQuery, pageNumber + 1, pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1 && !filterReplaceModalIsLoading) {
      fetchFilteredRecipes(searchQuery, pageNumber - 1, pageSize);
    }
  };

  // Initialize recipes when modal opens
  useEffect(() => {
    if (show) {
      dispatch(resetModalPagination());
      fetchFilteredRecipes("", 1, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedMeals]);

  useEffect(() => {
    if (show && Array.isArray(selectedRecipes)) {
      const normalizedSelected = selectedRecipes.map((item) => {
        // normalize regardless of structure
        const normalized = extractRecipeData(item);

        // if it's already structured (has mealName/recipe), keep as is
        const originalRecipeItem =
          item.recipe && item.mealName
            ? item
            : createOriginalStructuredItem(item);

        return {
          ...normalized,
          originalItem: originalRecipeItem,
        };
      });

      setSelectedList(normalizedSelected);
      // setOriginalRecipesList(recipesList);
      setFilteredRecipesList(recipesList);
      setIndexToReplace(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, selectedRecipes, recipesList]);

  const onRecipeReplace = (index) => {
    setIndexToReplace(index);
  };

  // Helper function to create original structured data format
  const createOriginalStructuredItem = (recipeToAdd) => {
    const normalizedRecipe = extractRecipeData(recipeToAdd);

    return {
      mealName: mealType,
      recipeId: normalizedRecipe.id,
      recipe: {
        title: normalizedRecipe.title,
        prepTime:
          normalizedRecipe.prepTime ||
          recipeToAdd.prepTime ||
          normalizedRecipe.totalTime ||
          recipeToAdd.totalTime ||
          "",
        servings: normalizedRecipe.servings || recipeToAdd.servings || "0",
        ingredientsCount:
          normalizedRecipe.ingra || recipeToAdd.ingredientsCount || "0",
        course: normalizedRecipe.course || recipeToAdd.course || "",
        difficulty: normalizedRecipe.difficulty || recipeToAdd.difficulty || "",
        ingredients:
          normalizedRecipe.ingredients || recipeToAdd.ingredients || [],
        instructions:
          normalizedRecipe.instructions || recipeToAdd.instructions || [],
        nutritionPerServing: {
          carbs_g:
            normalizedRecipe.nutrientsPerServing?.carbs?.toString() ||
            recipeToAdd.nutritionPerServing?.carbs_g ||
            recipeToAdd.nutrientsPerServing?.carbs_g ||
            "0",
          carbs_pct:
            recipeToAdd.nutritionPerServing?.carbs_pct ||
            recipeToAdd.nutrientsPerServing?.carbs_pct ||
            "0",
          protein_g:
            normalizedRecipe.nutrientsPerServing?.protein?.toString() ||
            recipeToAdd.nutritionPerServing?.protein_g ||
            recipeToAdd.nutrientsPerServing?.protein_g ||
            "0",
          protein_pct:
            recipeToAdd.nutritionPerServing?.protein_pct ||
            recipeToAdd.nutrientsPerServing?.protein_pct ||
            "0",
          fat_g:
            normalizedRecipe.nutrientsPerServing?.fat?.toString() ||
            recipeToAdd.nutritionPerServing?.fat_g ||
            recipeToAdd.nutrientsPerServing?.fat_g ||
            "0",
          fat_pct:
            recipeToAdd.nutritionPerServing?.fat_pct ||
            recipeToAdd.nutrientsPerServing?.fat_pct ||
            "0",
          calories:
            normalizedRecipe.nutrientsPerServing?.calories?.toString() ||
            recipeToAdd.nutritionPerServing?.calories ||
            recipeToAdd.nutrientsPerServing?.calories ||
            "0",
          calcium:
            recipeToAdd.nutritionPerServing?.calcium ||
            recipeToAdd.nutrientsPerServing?.calcium ||
            "0",
          iron:
            recipeToAdd.nutritionPerServing?.iron ||
            recipeToAdd.nutrientsPerServing?.iron ||
            "0",
          magnesium:
            recipeToAdd.nutritionPerServing?.magnesium ||
            recipeToAdd.nutrientsPerServing?.magnesium ||
            "0",
          potassium:
            recipeToAdd.nutritionPerServing?.potassium ||
            recipeToAdd.nutrientsPerServing?.potassium ||
            "0",
          sodium:
            recipeToAdd.nutritionPerServing?.sodium ||
            recipeToAdd.nutrientsPerServing?.sodium ||
            "0",
          sugar:
            recipeToAdd.nutritionPerServing?.sugar ||
            recipeToAdd.nutrientsPerServing?.sugar ||
            "0",
          vitaminC:
            recipeToAdd.nutritionPerServing?.vitaminC ||
            recipeToAdd.nutrientsPerServing?.vitaminC ||
            "0",
        },
        ingredientList:
          normalizedRecipe.ingredientList || recipeToAdd.ingredientList || [],

        recipeId: normalizedRecipe.id,
        mainImage: normalizedRecipe.mainImage || recipeToAdd.mainImage || null,
        alergies: recipeToAdd.alergies || recipeToAdd.allergies || "",
        preferences: recipeToAdd.preferences || "",
      },
    };
  };

  const onRecipeAdd = (recipeToAdd) => {
    const normalizedRecipe = extractRecipeData(recipeToAdd);
    const originalRecipeItem = createOriginalStructuredItem(recipeToAdd);

    const wrappedRecipe = {
      ...normalizedRecipe,
      originalItem: originalRecipeItem,
    };

    if (indexToReplace == null) {
      if (selectedList.length >= numberOfRecipesPerMeal) {
        setToastConfig({
          type: CONSTANTS.TOAST_ERROR,
          message: `You can only select up to ${numberOfRecipesPerMeal} recipes.`,
          heading: trans("Common.failed"),
        });
        setTimeout(() => setToastConfig(null), CONSTANTS.TOAST_DURATION_SHORT);
        return;
      }

      // safer duplicate check
      const exists = selectedList.some(
        (item) =>
          extractRecipeData(item.originalItem).title === normalizedRecipe.title
      );

      if (exists) {
        setToastConfig({
          type: CONSTANTS.TOAST_ERROR,
          message: "Recipe already exists in the selected list.",
          heading: trans("Common.failed"),
        });
        setTimeout(() => setToastConfig(null), CONSTANTS.TOAST_DURATION_SHORT);
        return;
      }

      const updated = [...selectedList, wrappedRecipe];
      setSelectedList(updated);
      onSelectedRecipesChange(updated.map((item) => item.originalItem));
    } else {
      // safer duplicate check for replacement
      const exists = selectedList.some(
        (item, i) =>
          extractRecipeData(item.originalItem).title ===
            normalizedRecipe.title && i !== indexToReplace
      );

      if (exists) {
        setToastConfig({
          type: CONSTANTS.TOAST_ERROR,
          message: "Recipe already exists in the selected list.",
          heading: trans("Common.failed"),
        });
        setTimeout(() => setToastConfig(null), CONSTANTS.TOAST_DURATION_SHORT);
        return;
      }

      const updated = [...selectedList];
      updated[indexToReplace] = wrappedRecipe;
      setSelectedList(updated);
      onSelectedRecipesChange(updated.map((item) => item.originalItem));
      setIndexToReplace(null);
    }
  };

  const handleRecipeRemove = (indexToRemove) => {
    if (selectedList.length <= 1) {
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: trans("ClientMealPlan.atLeastOneRecipeSelected"),
        heading: trans("Common.failed"),
      });
      setTimeout(() => setToastConfig(null), CONSTANTS.TOAST_DURATION_SHORT);
      return;
    }

    const updated = selectedList.filter((_, index) => index !== indexToRemove);
    setSelectedList(updated);
    onSelectedRecipesChange(updated.map((item) => item.originalItem));

    if (indexToRemove === indexToReplace) {
      setIndexToReplace(null);
    }
  };

  // handle search with pagination reset
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (debounceTimeout) clearTimeout(debounceTimeout);
    setDebounceTimeout(
      setTimeout(() => {
        // Reset pagination on search
        dispatch(resetModalPagination());
        if (value?.length >= 3) {
          fetchFilteredRecipes(value, 1, pageSize);
        } else if (!value || value.length === 0) {
          fetchFilteredRecipes("", 1, pageSize);
        }
      }, 400)
    );
  };

  const renderRecipeList = () => {
    if (filterReplaceModalIsLoading) {
      return !filterReplaceModalError ? (
        <Loader message="" />
      ) : (
        <p className="text-danger">There was an error loading the data.</p>
      );
    }

    if (filteredRecipesList?.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="mt-2">No recipes found</p>
        </div>
      );
    }

    const recipeDetailsChunks = chunkArray(filteredRecipesList, 2);

    return recipeDetailsChunks.map((chunk, rowIdx) => (
      <Row key={rowIdx} className={rowIdx > 0 ? "mt-3" : ""}>
        {chunk.map((recipe) => {
          const recipeData = extractRecipeData(recipe);
          return (
            <Col md={6} key={recipeData.id} className="mb-2">
              <Card
                className="d-flex flex-row align-items-center p-2"
                onClick={() => onRecipeAdd(recipe)}
                style={{ cursor: "pointer" }}
              >
                <div className="m-1">
                  <img
                    src={recipeData.mainImage || "/placeholder-image.jpg"}
                    alt={recipeData.title}
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                </div>
                <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-start ms-2">
                  <h6 className="mb-1">{recipeData.title}</h6>
                  <p className="mb-1 text-muted">
                    {recipeData.nutrientsPerServing?.calories
                      ? `${recipeData.nutrientsPerServing.calories} kcal`
                      : "N/A"}{" "}
                    | P {recipeData.nutrientsPerServing?.protein_g || 0} | C{" "}
                    {recipeData.nutrientsPerServing?.carbs_g || 0} | F{" "}
                    {recipeData.nutrientsPerServing?.fat_g || 0}
                  </p>
                  <small className="text-secondary">
                    Time: {recipeData.totalTime}
                  </small>
                </div>
                <div>
                  <i className="bi bi-plus-circle-fill fs-1 text-color"></i>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    ));
  };

  // Set filtered recipes list when data changes
  useEffect(() => {
    setFilteredRecipesList(recipesList || []);
  }, [recipesList]);

  // Clear debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      dialogClassName="recipes-details-custom-modal d-flex align-items-center justify-content-center"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">{`Selected recipes for ${mealType}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="d-flex flex-row flex-nowrap overflow-auto recipe-scroll-container gap-2 p-1 m-1">
          {selectedList.map((recipe, index) => (
            <div
              key={recipe?.id || recipe?.recipeId || index}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: indexToReplace === index ? "2px solid #007bff" : "none",
                borderRadius: "8px",
                padding: "8px 0px 8px 5px",
                width: "220px",
                flex: "0 0 auto",
                cursor: "pointer",
              }}
            >
              <div className="w-100">
                <RecipesCard
                  recipe={recipe}
                  isClickedFromSwapRecipe
                  onClick={() => onRecipeReplace(index)}
                />
              </div>
              <i
                className="bi bi-x-circle-fill text-danger fs-2"
                style={{
                  position: "absolute",
                  top: "0",
                  right: "6px",
                  cursor: "pointer",
                  borderRadius: "50%",
                }}
                onClick={() => handleRecipeRemove(index)}
              />
            </div>
          ))}
        </Row>

        <h5>Swap recipes with following recipes</h5>
        <hr />
        <Row className="mb-1">
          <Col xs={12} md={6}>
            <AutoComplete
              value={searchQuery}
              onChange={(value) => handleSearchChange(value)}
              placeholder="Search recipes by name"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              allowClear
              options={[]}
              className="rounded-3 border border-dark w-100 mb-2"
            />
          </Col>
        </Row>

        {/* Recipe count and pagination info */}

        <div
          style={{ maxHeight: "400px", overflowY: "auto", overflowX: "hidden" }}
        >
          {renderRecipeList()}
        </div>

        {/* Pagination Controls */}
        {filteredRecipesList?.length > 0 && (
          <Row className="mt-3 w-100 d-flex justify-content-between align-items-center">
            <div className="w-100 d-flex justify-content-between align-items-center mb-2">
              <div className="text-muted">
                {/* <small>
                  Page {pageNumber} • {filteredRecipesList.length} recipes
                </small> */}
              </div>
              <div>
                <button
                  className="btn btn-outline-primary btn-sm me-2"
                  disabled={pageNumber === 1 || filterReplaceModalIsLoading}
                  onClick={handlePreviousPage}
                >
                  Prev
                </button>
                {/* <span className="me-2 ms-2 text-muted small">
                  {pageNumber}
                </span> */}
                <button
                  className="btn btn-outline-primary btn-sm"
                  disabled={!hasMore || filterReplaceModalIsLoading}
                  onClick={handleNextPage}
                >
                  Next
                </button>
              </div>
            </div>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-primary"
          onClick={onHide}
          size="sm"
          className="back_button w-25 p-2"
          disabled={isUpdating}
        >
          {trans("Common.cancel")}
        </Button>
        <Button
          className="saveButton w-25 ml-2"
          onClick={handleUpdateWithSelectedRecipes}
          disabled={isUpdating || selectedList.length === 0}
        >
          {isUpdating ? "Updating..." : trans("Common.update")}
        </Button>
      </Modal.Footer>
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </Modal>
  );
};

export default ReplaceRecipeModal;
